import { z } from 'zod';
import { ENTITY_STATUSES, HOURS_FORMATS, USER_ROLES, USER_STATUSES } from '../enums.js';
import { isoDateSchema, paginated, paginationQuerySchema, sortQuerySchema, timeZoneSchema, uuidSchema, } from './common.js';
import { emailSchema } from './auth.js';
export const createUserSchema = z.object({
    email: emailSchema,
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    role: z.enum(USER_ROLES).default('USER'),
    timeZone: timeZoneSchema.optional(),
    groupIds: z.array(uuidSchema).max(100).default([]),
});
export const updateUserSchema = z
    .object({
    email: emailSchema,
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    role: z.enum(USER_ROLES),
    timeZone: timeZoneSchema,
    groupIds: z.array(uuidSchema).max(100),
})
    .partial();
export const updateUserStatusSchema = z.object({
    status: z.enum(['ACTIVE', 'INACTIVE']),
});
export const userAdminSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    role: z.enum(USER_ROLES),
    status: z.enum(USER_STATUSES),
    timeZone: z.string(),
    groups: z.array(z.object({ id: uuidSchema, name: z.string() })),
    lastLoginAt: z.string().nullable(),
    anonymizedAt: z.string().nullable(),
    /** Waiting for the user to replace a password an administrator reset. */
    mustChangePassword: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * The temporary password, returned once and stored nowhere. The administrator reads it out to the
 * user; a second request generates a different one, because this is the only time it exists in
 * readable form.
 */
export const resetUserPasswordResultSchema = z.object({
    temporaryPassword: z.string(),
    user: userAdminSchema,
});
/**
 * What an organization's logo must be to be accepted.
 *
 * Landscape because of where it is used: a strip across the top-left of a report's first page and
 * a line in the settings form. A portrait logo in that slot either shrinks to nothing or pushes
 * the title off the page, so the shape is part of the contract rather than something CSS is left
 * to apologise for. Raster only — an SVG is a document that can carry script, and this file is
 * later embedded in the page Chromium renders.
 */
export const LOGO_MAX_BYTES = 2 * 1024 * 1024;
export const LOGO_MIN_WIDTH = 80;
/** Wider than this and it is a banner, not a logo; the header would have nowhere to put it. */
export const LOGO_MAX_ASPECT_RATIO = 8;
export const LOGO_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const organizationLogoSchema = z.object({
    /** Same-origin path the browser fetches; changes whenever the image does. */
    url: z.string(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
});
export const organizationSettingsSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    weekStartDay: z.number().int().min(0).max(6),
    defaultTimeZone: z.string(),
    hoursFormat: z.enum(HOURS_FORMATS),
    locale: z.string(),
    /**
     * The closed period: regular users cannot add, change or delete hours dated before this day
     * (`isTimesheetLocked`). `null` closes nothing. Administrators are never subject to it.
     */
    timesheetLockDate: isoDateSchema.nullable(),
    logo: organizationLogoSchema.nullable(),
});
export const updateOrganizationSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    weekStartDay: z.number().int().min(0).max(6),
    defaultTimeZone: timeZoneSchema,
    hoursFormat: z.enum(HOURS_FORMATS),
    locale: z.string().trim().min(2).max(10),
    /** Omitted leaves the closed period as it is; `null` re-opens everything. */
    timesheetLockDate: isoDateSchema.nullable(),
})
    .partial();
export const USER_SORT_FIELDS = [
    'lastName',
    'firstName',
    'email',
    'role',
    'status',
    'createdAt',
    'lastLoginAt',
];
export const listUsersQuerySchema = paginationQuerySchema.extend({
    q: z.string().trim().max(100).optional(),
    role: z.enum(USER_ROLES).optional(),
    status: z.enum(USER_STATUSES).optional(),
    groupId: uuidSchema.optional(),
    sort: sortQuerySchema(USER_SORT_FIELDS),
});
export const userListSchema = paginated(userAdminSchema);
export const hexColorSchema = z
    .string()
    .trim()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a #RRGGBB colour')
    .transform((v) => v.toUpperCase());
export const projectAdminSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    code: z.string().nullable(),
    clientName: z.string().nullable(),
    description: z.string().nullable(),
    color: z.string(),
    status: z.enum(ENTITY_STATUSES),
    groupCount: z.number().int().nonnegative(),
    entryCount: z.number().int().nonnegative(),
    /** Hours reported against the project, so an admin can see which colours are in play. */
    totalMinutes: z.number().int().nonnegative(),
});
export const PROJECT_SORT_FIELDS = ['name', 'code', 'status', 'totalMinutes'];
export const listProjectsQuerySchema = paginationQuerySchema.extend({
    q: z.string().trim().max(100).optional(),
    status: z.enum(ENTITY_STATUSES).optional(),
    groupId: uuidSchema.optional(),
    sort: sortQuerySchema(PROJECT_SORT_FIELDS),
});
export const projectListSchema = paginated(projectAdminSchema);
export const anonymizeUserSchema = z.object({
    /** Typed by the admin in the confirmation dialog; the word is the safety catch. */
    confirm: z.literal('ANONYMIZE'),
});
export const importUsersQuerySchema = z.object({
    dryRun: z
        .union([z.boolean(), z.string()])
        .default(false)
        .transform((v) => typeof v === 'boolean' ? v : ['1', 'true', 'yes'].includes(v.toLowerCase())),
});
export const importUsersResultSchema = z.object({
    dryRun: z.boolean(),
    created: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    errors: z.array(z.object({ row: z.number().int(), message: z.string() })),
});
/** Everything held about one person, for GDPR access requests (self-service or admin). */
export const personalDataExportSchema = z.object({
    exportedAt: z.string(),
    user: userAdminSchema,
    organization: z.object({ id: uuidSchema, name: z.string() }),
    timeEntries: z.array(z.record(z.string(), z.unknown())),
    personalActivities: z.array(z.record(z.string(), z.unknown())),
    sessions: z.array(z.record(z.string(), z.unknown())),
    auditTrail: z.array(z.record(z.string(), z.unknown())),
});
//# sourceMappingURL=admin.js.map